// In-world loot for one room: '?' orbs with a reveal, item holograms with a
// compact comparison card (E equip / X salvage, swaps drop the old item so they
// are reversible), scrap/health drops, and the paused choice-of-three overlays.

import Phaser from 'phaser';
import { DEPTH, FONT, SCENES } from '../../config/constants';
import { COLORS, css, rarityColor } from '../../config/palette';
import type { GameContext, LootApi, RewardSceneData } from '../../core/contracts';
import { Subscriptions } from '../../core/events';
import { save } from '../../core/save';
import type { ItemInstance, LootSource, MajorUpgradeId, OrbType } from '../../core/types';
import { createItemCard } from '../../ui/ItemCard';
import { describeItem } from './describe';
import { salvageValueOf } from './inventory';
import { rollChoices, rollMajorUpgrades, rollReward, rollShop, type LootRollContext } from './rolls';
import { LOOT_TEX, ORB_COLOR, ensureLootTextures, softKey, texOr } from './textures';

interface Orb {
  type: OrbType;
  source: LootSource;
  x: number;
  y: number;
  baseY: number;
  vy: number;
  dropping: boolean;
  t: number;
  shell: Phaser.GameObjects.Image;
  rim: Phaser.GameObjects.Image;
  glow: Phaser.GameObjects.Image;
  mark: Phaser.GameObjects.Text;
  opening: number; // < 0 idle, else reveal timer
}

interface Pickup {
  item: ItemInstance;
  x: number;
  y: number;
  base: Phaser.GameObjects.Image;
  icon: Phaser.GameObjects.Image;
  glow: Phaser.GameObjects.Image;
  name: Phaser.GameObjects.Text;
  card: Phaser.GameObjects.Container | null;
  remove: () => void;
  t: number;
}

interface Drop {
  kind: 'scrap' | 'health';
  amount: number;
  img: Phaser.GameObjects.Image;
  vx: number;
  vy: number;
  t: number;
  resting: boolean;
}

const ORB_TOUCH = 64;
const CARD_RANGE = 180;

export function createLoot(ctx: GameContext): LootApi {
  const scene = ctx.scene;
  const subs = new Subscriptions();
  const orbs: Orb[] = [];
  const pickups: Pickup[] = [];
  const drops: Drop[] = [];
  ensureLootTextures(scene);

  const rollCtx = (): LootRollContext => ({
    rng: ctx.run.rng.loot,
    pity: ctx.run.pity,
    inventory: ctx.run.inventory,
    mech: ctx.run.mech,
    isUnlocked: (id) => save.isUnlocked(id),
    stats: ctx.run.stats,
    avoid: pickups.map((p) => p.item.defId),
  });
  const kitTags = () => ctx.run.mech.kitTags;

  /** A free floor spot near x (so revealed items never land in a wall). */
  const floorNear = (x: number, y: number, side: number): { x: number; y: number } => {
    for (const dx of [side * 110, -side * 110, side * 190, -side * 190, 0]) {
      const px = x + dx;
      if (ctx.world.isSolidAt(px, y - 40)) continue;
      const g = ctx.world.groundBelow(px, y - 40, 600);
      if (g !== null) return { x: px, y: g };
    }
    return { x, y };
  };

  // ───────────── orbs ─────────────
  function spawnOrb(type: OrbType, x: number, y: number, opts: { source?: LootSource; dropIn?: boolean } = {}): void {
    const color = ORB_COLOR[type];
    const shellKey = texOr(scene, LOOT_TEX.shell(type), texOr(scene, `orb_${type}`, softKey(scene)));
    const glow = scene.add.image(x, y, softKey(scene)).setTint(color).setBlendMode(Phaser.BlendModes.ADD).setDepth(DEPTH.PICKUPS - 0.1).setScale(2.2).setAlpha(0.55);
    const shell = scene.add.image(x, y, shellKey).setDepth(DEPTH.PICKUPS).setScale(0.9);
    const rim = scene.add.image(x, y, texOr(scene, LOOT_TEX.rim, softKey(scene))).setTint(color).setBlendMode(Phaser.BlendModes.ADD).setDepth(DEPTH.PICKUPS + 0.1).setScale(0.95);
    const mark = scene.add
      .text(x, y, '?', { fontFamily: FONT.display, fontSize: '58px', fontStyle: '700', color: '#ffffff', stroke: css(color), strokeThickness: 6 })
      .setOrigin(0.5)
      .setDepth(DEPTH.PICKUPS + 0.2)
      .setShadow(0, 0, css(color), 18, true, true);
    const restY = y;
    orbs.push({
      type,
      source: opts.source ?? 'room',
      x,
      y: opts.dropIn ? y - 700 : y,
      baseY: restY,
      vy: 0,
      dropping: !!opts.dropIn,
      t: Math.random() * 6,
      shell,
      rim,
      glow,
      mark,
      opening: -1,
    });
  }

  function updateOrb(o: Orb, dt: number): boolean {
    o.t += dt;
    if (o.dropping) {
      o.vy += 2600 * dt;
      o.y += o.vy * dt;
      if (o.y >= o.baseY) {
        o.y = o.baseY;
        o.dropping = false;
        ctx.fx.ring(o.x, o.y + 30, 90, { color: ORB_COLOR[o.type], duration: 300 });
        ctx.audio.play('land', { volume: 0.5, pitch: 1.4 });
      } else if (Math.random() < 0.6) ctx.fx.embers(o.x, o.y, { count: 1, color: ORB_COLOR[o.type] });
    }
    const bob = o.dropping ? 0 : Math.sin(o.t * 2.2) * 8;
    const y = o.y + bob;
    if (o.opening >= 0) {
      o.opening += dt;
      const k = o.opening / 0.35;
      const s = 0.9 + k * 0.5;
      o.shell.setScale(s).setRotation(o.opening * 18);
      o.rim.setScale(s * 1.05).setRotation(-o.opening * 24);
      o.glow.setScale(2.2 + k * 2).setAlpha(0.55 + k * 0.4);
      o.mark.setScale(1 + k * 0.6).setAlpha(1 - k);
      if (k >= 1) {
        reveal(o);
        return false;
      }
    } else {
      o.shell.setRotation(Math.sin(o.t * 0.8) * 0.15);
      o.rim.setRotation(o.t * 0.6);
      o.glow.setAlpha(0.45 + 0.2 * Math.sin(o.t * 3));
      o.mark.setScale(1 + 0.06 * Math.sin(o.t * 4));
      const p = ctx.player;
      if (!o.dropping && p?.alive && Math.hypot(p.x - o.x, p.y - y) < ORB_TOUCH + 30) {
        o.opening = 0;
        ctx.audio.play('orb_open');
      }
    }
    for (const g of [o.shell, o.rim, o.glow, o.mark]) g.setPosition(o.x, y);
    return true;
  }

  function reveal(o: Orb): void {
    const color = ORB_COLOR[o.type];
    for (const g of [o.shell, o.rim, o.glow, o.mark]) g.destroy();
    ctx.fx.ring(o.x, o.y, 160, { color, duration: 360, width: 8 });
    ctx.fx.debris(o.x, o.y, { count: 10, color });
    ctx.fx.flash(color, 0.12, 160);
    ctx.time.hitstop(60);
    ctx.run.stats.orbsOpened += 1;
    ctx.bus.emit('orb:collected', { type: o.type, x: o.x, y: o.y });

    const item = rollReward(rollCtx(), o.type, o.source);
    const rare = item.rarity !== 'standard';
    ctx.audio.play(rare ? 'loot_rare' : 'loot_reveal');
    const inv = ctx.run.inventory;
    if (inv.canAutoEquip(item)) {
      inv.equip(item);
      inv.changed();
      const name = describeItem(item).name;
      ctx.bus.emit('ui:toast', { text: `EQUIPPED  ${name}`, color: rarityColor(item.rarity) });
      ctx.fx.floatText(o.x, o.y - 60, name.toUpperCase(), { color: rarityColor(item.rarity), size: 30, duration: 1600 });
      ctx.audio.play('equip');
      ctx.bus.emit('item:equipped', { item, replaced: null });
      ctx.bus.emit('loot:revealed', { item, autoEquipped: true });
    } else {
      const p = ctx.player;
      const side = p && p.x > o.x ? -1 : 1;
      const spot = floorNear(o.x, Math.max(o.y + 60, p?.feetY ?? o.y), side);
      spawnItemPickup(item, spot.x, spot.y, { pop: true });
      ctx.bus.emit('loot:revealed', { item, autoEquipped: false });
    }
  }

  // ───────────── item pickups ─────────────
  function iconFor(item: ItemInstance): { key: string; frame?: string | number; tint?: number; scale: number } {
    try {
      const d = describeItem(item).icon;
      if (scene.textures.exists(d.key)) return { key: d.key, frame: d.frame, tint: d.tint, scale: d.scale ?? 1 };
    } catch {
      /* fall through */
    }
    return { key: softKey(scene), tint: rarityColor(item.rarity), scale: 1 };
  }

  function spawnItemPickup(item: ItemInstance, x: number, y: number, opts: { pop?: boolean } = {}): void {
    const color = rarityColor(item.rarity);
    const display = (() => {
      try {
        return describeItem(item);
      } catch {
        return null;
      }
    })();
    const base = scene.add.image(x, y, texOr(scene, LOOT_TEX.holoBase, softKey(scene))).setOrigin(0.5, 1).setDepth(DEPTH.PICKUPS);
    const glow = scene.add.image(x, y - 50, softKey(scene)).setTint(color).setBlendMode(Phaser.BlendModes.ADD).setScale(1.8).setAlpha(0.35).setDepth(DEPTH.PICKUPS - 0.1);
    const ic = iconFor(item);
    const icon = scene.add.image(x, y - 56, ic.key, ic.frame).setDepth(DEPTH.PICKUPS + 0.1);
    const fit = 72 / Math.max(icon.width, icon.height, 1);
    icon.setScale(Math.min(2.5, fit * 1.4) * (ic.scale || 1));
    if (ic.tint !== undefined) icon.setTint(ic.tint);
    const name = scene.add
      .text(x, y - 108, (display?.name ?? item.defId).toUpperCase(), { fontFamily: FONT.display, fontSize: '16px', fontStyle: '700', color: css(color), stroke: '#05070b', strokeThickness: 4 })
      .setOrigin(0.5)
      .setDepth(DEPTH.PICKUPS + 0.2);

    const pk: Pickup = { item, x, y, base, icon, glow, name, card: null, remove: () => undefined, t: Math.random() * 4 };
    const value = salvageValueOf(item);
    pk.remove = ctx.interact.add({
      x,
      y: y - 50,
      radius: 110,
      prompt: 'EQUIP',
      altPrompt: `SALVAGE +${value}`,
      priority: 1,
      onInteract: () => equipPickup(pk),
      onAlt: () => salvagePickup(pk, value),
    });
    pickups.push(pk);
    if (opts.pop) {
      for (const g of [icon, name]) {
        g.setAlpha(0);
        scene.tweens.add({ targets: g, alpha: 1, duration: 300 });
      }
      icon.setScale(icon.scaleX * 0.2);
      scene.tweens.add({ targets: icon, scale: icon.scaleX * 5, duration: 320, ease: 'Back.Out' });
    }
  }

  function removePickup(pk: Pickup): void {
    pk.remove();
    for (const g of [pk.base, pk.icon, pk.glow, pk.name]) g.destroy();
    pk.card?.destroy();
    const i = pickups.indexOf(pk);
    if (i >= 0) pickups.splice(i, 1);
  }

  function equipPickup(pk: Pickup): void {
    const inv = ctx.run.inventory;
    removePickup(pk);
    const replaced = inv.equip(pk.item);
    inv.changed();
    ctx.audio.play('equip');
    ctx.fx.ring(pk.x, pk.y - 50, 90, { color: rarityColor(pk.item.rarity), duration: 260 });
    ctx.bus.emit('item:equipped', { item: pk.item, replaced });
    if (replaced) spawnItemPickup(replaced, pk.x, pk.y, { pop: true });
  }

  function salvagePickup(pk: Pickup, value: number): void {
    removePickup(pk);
    ctx.run.addScrap(value);
    ctx.audio.play('salvage');
    ctx.fx.debris(pk.x, pk.y - 40, { count: 6, color: COLORS.scrap });
    ctx.fx.floatText(pk.x, pk.y - 90, `+${value} SCRAP`, { color: COLORS.scrap });
    ctx.bus.emit('item:salvaged', { item: pk.item, scrap: value });
  }

  function updatePickup(pk: Pickup, dt: number): void {
    pk.t += dt;
    const fy = pk.y - 56 + Math.sin(pk.t * 2) * 5;
    pk.icon.setY(fy);
    pk.glow.setAlpha(0.3 + 0.12 * Math.sin(pk.t * 3));
    const p = ctx.player;
    const near = !!p?.alive && Math.hypot(p.x - pk.x, p.y - (pk.y - 50)) < CARD_RANGE;
    if (near && !pk.card) {
      try {
        const comparison = ctx.run.inventory.compare(pk.item, kitTags());
        pk.card = createItemCard(scene, pk.item, { compact: true, comparison, controls: { equip: 'EQUIP', salvage: `SALVAGE +${salvageValueOf(pk.item)}` } });
        pk.card.setDepth(DEPTH.WORLD_UI);
      } catch (err) {
        console.warn('[loot] item card unavailable', err);
        pk.card = scene.add.container(0, 0).setDepth(DEPTH.WORLD_UI);
      }
    } else if (!near && pk.card) {
      pk.card.destroy();
      pk.card = null;
    }
    if (pk.card) {
      const h = pk.card.getBounds().height || 200;
      pk.card.setPosition(pk.x, pk.y - 140 - h / 2);
    }
  }

  // ───────────── scrap / health ─────────────
  function spawnDrop(kind: 'scrap' | 'health', x: number, y: number, amount: number): void {
    const key = kind === 'scrap' ? texOr(scene, LOOT_TEX.scrap, softKey(scene)) : texOr(scene, LOOT_TEX.health, softKey(scene));
    const img = scene.add.image(x, y, key).setDepth(DEPTH.PICKUPS);
    if (key === softKey(scene)) img.setTint(kind === 'scrap' ? COLORS.scrap : COLORS.heal).setScale(0.4);
    drops.push({ kind, amount, img, vx: (Math.random() - 0.5) * 360, vy: -350 - Math.random() * 300, t: 0, resting: false });
  }

  function updateDrop(d: Drop, dt: number): boolean {
    d.t += dt;
    const p = ctx.player;
    const img = d.img;
    if (p?.alive) {
      const dx = p.x - img.x;
      const dy = p.y - img.y;
      const dist = Math.hypot(dx, dy);
      if (d.t > 0.35 && dist < p.stats.pickupRadius) {
        const sp = 900 + (p.stats.pickupRadius - dist) * 6;
        img.x += (dx / (dist || 1)) * sp * dt;
        img.y += (dy / (dist || 1)) * sp * dt;
        d.resting = false;
        if (dist < 30) {
          if (d.kind === 'scrap') {
            ctx.run.addScrap(d.amount);
            ctx.audio.play('scrap_pickup', { volume: 0.5, pitch: 0.9 + Math.random() * 0.3 });
          } else {
            p.heal(d.amount);
            ctx.audio.play('heal');
            ctx.fx.floatText(p.x, p.y - 70, `+${d.amount}`, { color: COLORS.heal });
          }
          img.destroy();
          return false;
        }
        return true;
      }
    }
    if (!d.resting) {
      d.vy += 2200 * dt;
      const nx = img.x + d.vx * dt;
      const ny = img.y + d.vy * dt;
      if (ctx.world.isSolidAt(nx, img.y)) d.vx *= -0.5;
      else img.x = nx;
      if (d.vy > 0 && (ctx.world.isSolidAt(img.x, ny + 8) || ctx.world.isOneWayAt(img.x, ny + 8))) {
        d.vy *= -0.35;
        d.vx *= 0.6;
        if (Math.abs(d.vy) < 80) d.resting = true;
      } else img.y = ny;
      img.rotation += d.vx * dt * 0.02;
      if (img.y > ctx.world.height + 200) {
        img.destroy();
        return false;
      }
    }
    if (d.t > 30) {
      img.destroy();
      return false;
    }
    return true;
  }

  // ───────────── overlays ─────────────
  function openReward(data: Omit<RewardSceneData, 'onPick'>): Promise<number | null> {
    return new Promise((resolve) => {
      const sm = scene.scene;
      sm.pause();
      sm.launch(SCENES.REWARD, {
        ...data,
        onPick: (i: number | null) => {
          sm.resume(SCENES.GAME);
          resolve(i);
        },
      } satisfies RewardSceneData);
      sm.bringToTop(SCENES.REWARD);
    });
  }

  const api: LootApi = {
    spawnOrb,
    roll: (type, source) => rollReward(rollCtx(), type, source),
    rollChoices: (type, source, n) => rollChoices(rollCtx(), type, source, n),
    rollShop: (n) => rollShop(rollCtx(), n, ctx.run.sectorIndex),
    rollMajorUpgrades: (n) => rollMajorUpgrades(rollCtx(), n),
    spawnItemPickup,
    spawnScrap(x, y, amount) {
      let left = Math.max(1, Math.round(amount));
      const chunks = Math.min(6, Math.max(1, Math.ceil(left / 4)));
      for (let i = 0; i < chunks; i++) {
        const n = i === chunks - 1 ? left : Math.ceil(left / (chunks - i));
        left -= n;
        spawnDrop('scrap', x, y, n);
      }
    },
    spawnHealth(x, y, amount) {
      if (ctx.run.hasChallenge('scarcity')) return;
      spawnDrop('health', x, y, amount);
    },

    async offerChoice(items, title, opts = {}) {
      if (!items.length) return null;
      const inv = ctx.run.inventory;
      const comparisons = items.map((it) => inv.compare(it, kitTags()));
      ctx.audio.play('loot_rare');
      const i = await openReward({ title, subtitle: opts.subtitle ?? 'CHOOSE ONE', items, comparisons, allowSkip: opts.allowSkip ?? true });
      if (i === null || !items[i]) return null;
      const item = items[i];
      ctx.run.stats.itemsFound += 1;
      const replaced = inv.equip(item);
      inv.changed();
      ctx.audio.play('equip');
      ctx.bus.emit('item:equipped', { item, replaced });
      const p = ctx.player;
      if (replaced && p) {
        const spot = floorNear(p.x, p.feetY, p.facing);
        spawnItemPickup(replaced, spot.x, spot.y, { pop: true });
      }
      return item;
    },

    async offerMajorUpgrade() {
      const ups = rollMajorUpgrades(rollCtx(), 3);
      if (!ups.length) return null;
      const i = await openReward({ title: 'MAJOR UPGRADE', subtitle: 'THE CORE ADAPTS', upgrades: ups, allowSkip: false });
      const pick = i === null ? null : ups[i];
      if (!pick) return null;
      const inv = ctx.run.inventory;
      inv.addUpgrade(pick.id as MajorUpgradeId);
      inv.changed();
      ctx.bus.emit('ui:toast', { text: `UPGRADE  ${pick.name}`, color: COLORS.ultBar });
      return pick.id;
    },

    start() {
      subs.on('enemy:killed', ({ enemy, x, y, info }) => {
        if (info.source !== 'player' && info.source !== 'self') return;
        const p = ctx.player;
        if (enemy.scrapValue > 0) api.spawnScrap(x, y, enemy.scrapValue * (p?.stats.scrapGain ?? 1));
        const elite = enemy.elite.length > 0;
        if (enemy.kind === 'enemy' && ctx.rng.chance(elite ? 0.35 : 0.08)) api.spawnHealth(x, y, elite ? 25 : 12);
      });
    },

    update(dt) {
      for (let i = orbs.length - 1; i >= 0; i--) if (!updateOrb(orbs[i], dt)) orbs.splice(i, 1);
      for (const pk of [...pickups]) updatePickup(pk, dt);
      for (let i = drops.length - 1; i >= 0; i--) if (!updateDrop(drops[i], dt)) drops.splice(i, 1);
    },

    destroy() {
      subs.dispose();
      for (const o of orbs) for (const g of [o.shell, o.rim, o.glow, o.mark]) g.destroy();
      orbs.length = 0;
      for (const pk of [...pickups]) removePickup(pk);
      for (const d of drops) d.img.destroy();
      drops.length = 0;
    },
  };
  return api;
}
